import React from 'react';
import gql from 'graphql-tag';
import {
  isDesktop,
  isChrome,
  isFirefox,
  isSafari,
  isOpera,
  isIE,
  isEdge,
  isMobileOnly,
  isTablet,
  isWindows,
  isMacOs,
  isAndroid,
  isWinPhone,
  isIOS,
} from 'react-device-detect';
import moment from 'moment';

import AppContext, { AppContextType } from '../../context';
import s from './Layout.module.scss';
import { withRouter } from '../../util/withRouter';
import WebHeader from '../WebHeader';
import WebFooter from '../WebFooter';
import {
  trackingAllowed,
  onTrackingConsentChange,
} from '../../util/trackingConsent';

const logTrackingDataMutation = gql`
  mutation logTrackingData(
    $isNewVisit: Boolean!
    $duration: Int!
    $deviceInformation: String!
  ) {
    logTrackingData(
      isNewVisit: $isNewVisit
      duration: $duration
      deviceInformation: $deviceInformation
    ) {
      success
      message
    }
  }
`;

const logOffersClickMutation = gql`
  mutation logOffersClick($bundesland: String!, $mode: String!) {
    logOffersClick(bundesland: $bundesland, mode: $mode) {
      success
      message
    }
  }
`;

const logLocationMutation = gql`
  mutation logLocation($latitude: Float!, $longitude: Float!) {
    logLocation(latitude: $latitude, longitude: $longitude) {
      success
      message
    }
  }
`;

const VISIT_TIMEOUT_MS = 30 * 60; // 30 minutes in secods
const MIN_UPDATE_TRACKING_INTERVAL = 10 * 1000; // 10 seconds in ms
const TRACKING_TIME_FORMAT = 'DD.MM.YYYY HH:mm:ss';

export const userInteractionData = {
  siteInteraction: false,
};

export const trackingVariables = {
  lastUrl: null,
  lastUpdate: 0,
};

// re-exported so existing imports of trackingAllowed from Layout keep working
export { trackingAllowed };

export const getTrackingCookie = () => {
  if (trackingAllowed()) {
    const stored: string | null = sessionStorage.getItem('analyticsData');

    if (stored == null) {
      return {
        lastAccess: null,
        clickedOffers: [],
        clicked18PlusOffers: [],
        locationSent: false,
      };
    }

    const analyticsData = JSON.parse(stored);

    if (analyticsData.lastAccess != null) {
      const now = moment();
      const then = moment(analyticsData.lastAccess, TRACKING_TIME_FORMAT);
      const diffSeconds = now.diff(then, 'seconds');

      if (diffSeconds > VISIT_TIMEOUT_MS) {
        return {
          lastAccess: null,
          clickedOffers: [],
          clicked18PlusOffers: [],
          locationSent: false,
        };
      }
    }
    return analyticsData;
  }

  return null;
};

export const setTrackingCookie = (analyticsData: any) => {
  if (trackingAllowed()) {
    sessionStorage.setItem('analyticsData', JSON.stringify(analyticsData));
  }
};

export const getDeviceInformation = () => {
  if (trackingAllowed()) {
    let deviceType = 'others';
    if (isDesktop) deviceType = 'desktop';
    else if (isMobileOnly) deviceType = 'mobile';
    else if (isTablet) deviceType = 'tablet';

    let os = 'others';
    if (isDesktop && isWindows) os = 'windows';
    else if (isDesktop && isMacOs) os = 'mac';
    else if ((isMobileOnly || isTablet) && isAndroid) os = 'android';
    else if ((isMobileOnly || isTablet) && isIOS) os = 'ios';
    else if ((isMobileOnly || isTablet) && isWinPhone) os = 'winPhone';

    let browser = 'others';
    if (isChrome) browser = 'chrome';
    if (isFirefox) browser = 'firefox';
    if (isSafari) browser = 'safari';
    if (isOpera) browser = 'opera';
    if (isIE) browser = 'ie';
    if (isEdge) browser = 'edge';

    return {
      deviceType,
      os,
      browser,
    };
  }

  return null;
};

export const logTrackingData = async (
  context: AppContextType,
  force = false,
) => {
  if (trackingAllowed()) {
    const thisUpdate = new Date().getTime();

    if (
      !force &&
      thisUpdate - trackingVariables.lastUpdate < MIN_UPDATE_TRACKING_INTERVAL
    ) {
      return;
    }

    trackingVariables.lastUpdate = thisUpdate;

    const analyticsData = getTrackingCookie();
    const now = moment();

    if (analyticsData.lastAccess == null) {
      await context.client.mutate({
        mutation: logTrackingDataMutation,
        variables: {
          isNewVisit: true,
          duration: 0,
          deviceInformation: JSON.stringify(getDeviceInformation()),
        },
      });
    } else {
      const then = moment(analyticsData.lastAccess, TRACKING_TIME_FORMAT);
      const diffSeconds = now.diff(then, 'seconds');

      await context.client.mutate({
        mutation: logTrackingDataMutation,
        variables: {
          isNewVisit: false,
          duration: diffSeconds,
          locationEnabled: false, // already set on visit start
          latitude: 0, // already set on visit start
          longitude: 0, // already set on visit start
          deviceInformation: '', // already set on visit start
        },
      });
    }

    analyticsData.lastAccess = now.format(TRACKING_TIME_FORMAT);
    setTrackingCookie(analyticsData);
  }
};

export const logOffersClick = async (context: AppContextType, url: string) => {
  if (trackingAllowed()) {
    const href = decodeURIComponent(url);

    if (!href.includes('/orientation/angebote/')) return;

    const analyticsData = getTrackingCookie();
    if (!analyticsData.clickedOffers) {
      analyticsData.clickedOffers = [];
    }

    /* eslint-disable prettier/prettier */
    let bundesland = '';
    if (href.includes('/orientation/angebote/wien')) bundesland = 'wien';
    else if (href.includes('/orientation/angebote/oberösterreich')) bundesland = 'oberösterreich';
    else if (href.includes('/orientation/angebote/kärnten')) bundesland = 'kärnten';
    else if (href.includes('/orientation/angebote/steiermark')) bundesland = 'steiermark';
    else if (href.includes('/orientation/angebote/burgenland')) bundesland = 'burgenland';
    /* eslint-enable prettier/prettier */

    if (
      bundesland !== '' &&
      !analyticsData.clickedOffers.includes(bundesland)
    ) {
      analyticsData.clickedOffers.push(bundesland);
      setTrackingCookie(analyticsData);

      await context.client.mutate({
        mutation: logOffersClickMutation,
        variables: {
          bundesland,
          mode: 'young',
        },
      });
    }
  }
};

export const log18PlusOffersClick = async (
  context: AppContextType,
  url: string,
) => {
  if (trackingAllowed()) {
    const href = decodeURIComponent(url);
    if (!href.includes('/reorientation/angebote/')) return;

    const analyticsData = getTrackingCookie();
    if (!analyticsData.clicked18PlusOffers) {
      analyticsData.clicked18PlusOffers = [];
    }

    /* eslint-disable prettier/prettier */
    let bundesland = '';
    if (href.includes('/reorientation/angebote/wien')) bundesland = 'wien';
    else if (href.includes('/reorientation/angebote/oberösterreich')) bundesland = 'oberösterreich';
    else if (href.includes('/reorientation/angebote/salzburg')) bundesland = 'salzburg';
    else if (href.includes('/reorientation/angebote/vorarlberg')) bundesland = 'vorarlberg';
    else if (href.includes('/reorientation/angebote/steiermark')) bundesland = 'steiermark';
    else if (href.includes('/reorientation/angebote/burgenland')) bundesland = 'burgenland';
    /* eslint-enable prettier/prettier */

    if (
      bundesland !== '' &&
      !analyticsData.clicked18PlusOffers.includes(bundesland)
    ) {
      analyticsData.clicked18PlusOffers.push(bundesland);
      setTrackingCookie(analyticsData);

      await context.client.mutate({
        mutation: logOffersClickMutation,
        variables: {
          bundesland,
          mode: '18+',
        },
      });
    }
  }
};

export const logLocation = async (context: AppContextType) => {
  if (trackingAllowed()) {
    let analyticsData = getTrackingCookie();
    if (analyticsData.locationSent) return;

    if (navigator && navigator.geolocation) {
      // resolves once user accepts location service
      navigator.geolocation.getCurrentPosition(async (pos) => {
        if (
          pos.coords &&
          pos.coords.latitude != null &&
          pos.coords.longitude != null
        ) {
          // fetch again, if already changed
          analyticsData = getTrackingCookie();
          if (analyticsData.locationSent) return;

          analyticsData.locationSent = true;
          setTrackingCookie(analyticsData);

          await context.client.mutate({
            mutation: logLocationMutation,
            variables: {
              latitude: pos.coords.latitude,
              longitude: pos.coords.longitude,
            },
          });
        }
      });
    }

    /*
    // for testing
    analyticsData.locationSent = true;
    setTrackingCookie(analyticsData);

    await context.client.mutate({
      mutation: logLocationMutation,
      variables: {
        latitude: 50.0656392920582,
        longitude: 14.437840821138268,
        // latitude: 48.250443,
        // longitude: 16.4468171,
      },
    });
    */
  }
};

export const triggerNewVisit = (context: AppContextType) => {
  sessionStorage.removeItem('analyticsData');
  logTrackingData(context, true);
};

interface LayoutProps {
  children: React.ReactNode;
  location: Location;
}

class Layout extends React.Component<LayoutProps> {
  static contextType = AppContext;

  lastUrl: string | null = null;

  wasAllowed = false;

  unsubscribeConsent: (() => void) | null = null;

  constructor(props: any) {
    super(props);

    this.registerInteraction = this.registerInteraction.bind(this);
    this.onConsentChange = this.onConsentChange.bind(this);
  }

  componentDidMount() {
    this.wasAllowed = trackingAllowed();
    this.unsubscribeConsent = onTrackingConsentChange(this.onConsentChange);

    const currentUrl = window.location.href;

    // log statistics but ignore admin pages
    if (!currentUrl.includes('/admin')) {
      const logData = async () => {
        await logTrackingData(this.context as AppContextType, true);
        await logOffersClick(this.context as AppContextType, currentUrl);
        await log18PlusOffersClick(this.context as AppContextType, currentUrl);
        await logLocation(this.context as AppContextType);
      };
      logData();
    }

    document.addEventListener('mousedown', this.registerInteraction);
    document.addEventListener('touchstart', this.registerInteraction);
  }

  componentDidUpdate() {
    // make updates on subpage changes
    const currentUrl = window.location.href;

    // log statistics but ignore admin pages
    if (!currentUrl.includes('/admin')) {
      if (currentUrl != null && currentUrl !== this.lastUrl) {
        const logData = async () => {
          await logTrackingData(this.context as AppContextType);
          await logOffersClick(this.context as AppContextType, currentUrl);
          await log18PlusOffersClick(this.context as AppContextType, currentUrl); // eslint-disable-line prettier/prettier
          await logLocation(this.context as AppContextType);
        };
        logData();
      }
      this.lastUrl = window.location.href;
    }
  }

  componentWillUnmount() {
    if (this.unsubscribeConsent) this.unsubscribeConsent();
    document.removeEventListener('mousedown', this.registerInteraction);
    document.removeEventListener('touchstart', this.registerInteraction);
  }

  // called by trackingConsent.ts whenever the visitor changes their consent
  onConsentChange(allowed: boolean) {
    if (allowed && !this.wasAllowed) {
      // consent just granted: start a fresh visit and log the current page
      sessionStorage.removeItem('analyticsData');
      const ctx = this.context as AppContextType;
      const url = window.location.href;
      if (!url.includes('/admin')) {
        const logData = async () => {
          await logTrackingData(ctx, true);
          await logOffersClick(ctx, url);
          await log18PlusOffersClick(ctx, url);
          await logLocation(ctx);
        };
        logData();
      }
    } else if (!allowed && this.wasAllowed) {
      // consent withdrawn: drop the local tracking state
      sessionStorage.removeItem('analyticsData');
    }
    this.wasAllowed = allowed;
  }

  static getLayout(location: Location) {
    const section = location.pathname.split('/')[1];
    switch (section) {
      case '':
      case 'berufe-entdecken':
      case 'beruf':
        return {
          appContainerClass: s.appContainerFullscreen,
          appContentClass: s.appContentFullscreen,
          withWebHeader: true,
          withWebFooter: true,
        };
      case 'login':
      case 'registration':
      case 'warum-jopsy':
      case 'fakten':
      case 'mode-select':
      case 'impressum':
      case 'datenschutz':
        return {
          appContainerClass: s.appContainerFullscreen,
          appContentClass: `${s.appContentFullscreen} ${s.lightGreyBackground}`,
          withWebHeader: true,
          withWebFooter: true,
        };
      default:
        return {
          appContainerClass: s.appContainer,
          appContentClass: s.appContent,
          withWebHeader: false,
          withWebFooter: false,
        };
    }
  }

  // eslint-disable-next-line class-methods-use-this
  registerInteraction() {
    userInteractionData.siteInteraction = true;
  }

  render() {
    const { location } = this.props;

    const layout = Layout.getLayout(location);

    return (
      <div className={layout.appContainerClass}>
        {layout.withWebHeader && <WebHeader />}
        <div className={layout.appContentClass}>{this.props.children}</div>
        {layout.withWebFooter && <WebFooter />}
      </div>
    );
  }
}

export default withRouter(Layout);
